import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Mail, MessageCircle } from "lucide-react";
import Placeholder from "@/components/Placeholder";
import { FadeUp } from "@/components/Reveal";
import { SERVICES } from "@/data/services";
import { SITE } from "@/data/site";
import { useLanguage } from "@/i18n/LanguageContext";

export default function ServicePage() {
  const { t } = useLanguage();
  const { slug } = useParams();
  const index = Math.max(0, SERVICES.findIndex((s) => s.slug === slug));
  const meta = SERVICES[index];
  const service = t("services.items")[meta.slug] || t("services.items")[SERVICES[0].slug];
  const nextMeta = SERVICES[(index + 1) % SERVICES.length];
  const next = t("services.items")[nextMeta.slug];

  const subject = encodeURIComponent(`${t("servicesPage.proposalSubject")} — ${service.name}`);
  const meetingSubject = encodeURIComponent(`${t("servicesPage.meetingSubject")} — ${service.name}`);
  const waText = encodeURIComponent(`${t("servicesPage.waMessage")} ${service.name}.`);

  return (
    <main className="pb-28 pt-32 md:pb-40 md:pt-44" data-testid="service-page">
      <div className="mx-auto max-w-[1440px] px-6 md:px-12">
        <FadeUp>
          <Link
            to="/#services"
            className="group inline-flex items-center gap-3 font-spacemono text-[11px] uppercase tracking-[0.3em] text-white/50 transition-colors duration-300 hover:text-white"
            data-testid="service-back-link"
          >
            <ArrowLeft className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-x-1" />
            {t("servicesPage.back")}
          </Link>
        </FadeUp>

        <FadeUp delay={0.05}>
          <p className="mt-12 font-spacemono text-xs text-[#F97316]">
            ({t("servicesPage.prefix")} — {String(index + 1).padStart(2, "0")})
          </p>
          <h1
            className="mt-5 text-balance font-syne text-4xl font-extrabold uppercase leading-[1.02] tracking-tight sm:text-5xl lg:text-7xl"
            data-testid="service-title"
          >
            {service.name}
          </h1>
          <p className="mt-5 font-spacemono text-[11px] uppercase tracking-[0.3em] text-white/45">{service.tag}</p>
        </FadeUp>

        <div className="mt-16 grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-8">
          <FadeUp className="lg:col-span-7">
            <p className="max-w-xl text-base leading-relaxed text-white/60 md:text-lg" data-testid="service-description">
              {service.desc}
            </p>
          </FadeUp>
          <FadeUp delay={0.1} className="lg:col-span-5">
            <p className="mb-5 font-spacemono text-[10px] uppercase tracking-[0.3em] text-white/35">
              {t("servicesPage.deliverLabel")}
            </p>
            <ul data-testid="service-deliverables">
              {service.delivers.map((d, i) => (
                <li
                  key={d}
                  className={`flex items-center gap-4 border-t border-white/10 py-3.5 ${
                    i === service.delivers.length - 1 ? "border-b" : ""
                  }`}
                >
                  <span className="h-1 w-1 shrink-0 bg-[#F97316]" />
                  <span className="text-sm text-white/70">{d}</span>
                </li>
              ))}
            </ul>
          </FadeUp>
        </div>

        <FadeUp className="mt-16 md:mt-20">
          {meta.imgs.length > 0 ? (
            <figure>
              <div
                className={`grid gap-2 overflow-hidden ${
                  meta.imgs.length === 1
                    ? "max-w-3xl grid-cols-1"
                    : meta.imgs.length === 2
                      ? "max-w-4xl grid-cols-2"
                      : "max-w-4xl grid-cols-3"
                }`}
              >
                {meta.imgs.map((src) => (
                  <div key={src} className="overflow-hidden rounded-xl border border-white/10">
                    <img
                      src={src}
                      alt={service.imgCaption}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 ease-out hover:scale-[1.04]"
                    />
                  </div>
                ))}
              </div>
              <figcaption className="mt-4 font-spacemono text-[10px] uppercase tracking-[0.25em] text-white/35">
                {service.imgCaption}
              </figcaption>
            </figure>
          ) : (
            <Placeholder
              label={`${service.name} — ${t("servicesPage.photoComing")}`}
              className="aspect-[21/8] max-w-4xl rounded-2xl"
            />
          )}
        </FadeUp>

        <FadeUp className="mt-16 md:mt-24">
          <div
            className="flex flex-col gap-8 rounded-2xl border border-[#F97316]/25 bg-[#111111] p-8 md:flex-row md:items-center md:justify-between md:p-12"
            data-testid="service-cta"
          >
            <div>
              <h2 className="font-syne text-2xl font-bold tracking-tight md:text-3xl">{t("servicesPage.ctaTitle")}</h2>
              <p className="mt-2 text-sm text-white/50">{t("servicesPage.ctaSub")}</p>
            </div>
            <div className="flex flex-wrap gap-4">
              <a
                href={`mailto:${SITE.email}?subject=${subject}`}
                className="group flex items-center gap-3 rounded-full bg-[#F97316] px-7 py-3.5 font-spacemono text-[11px] uppercase tracking-[0.2em] text-white transition-[background-color,transform] duration-300 hover:bg-[#EA580C] active:scale-[0.96]"
                data-testid="service-cta-email"
              >
                <Mail className="h-4 w-4" />
                {t("servicesPage.ctaProposal")}
              </a>
              <a
                href={`mailto:${SITE.email}?subject=${meetingSubject}`}
                className="group flex items-center gap-3 rounded-full border border-white/20 px-7 py-3.5 font-spacemono text-[11px] uppercase tracking-[0.2em] text-white/80 transition-[border-color,color,transform] duration-300 hover:border-[#F97316] hover:text-white active:scale-[0.96]"
                data-testid="service-cta-meeting"
              >
                {t("servicesPage.ctaMeeting")}
              </a>
              <a
                href={`https://wa.me/${SITE.whatsappCorporate}?text=${waText}`}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-3 rounded-full border border-white/20 px-7 py-3.5 font-spacemono text-[11px] uppercase tracking-[0.2em] text-white/80 transition-[border-color,color,transform] duration-300 hover:border-[#F97316] hover:text-white active:scale-[0.96]"
                data-testid="service-cta-whatsapp"
              >
                <MessageCircle className="h-4 w-4" />
                {t("servicesPage.ctaWhatsApp")}
              </a>
            </div>
          </div>
        </FadeUp>

        <FadeUp className="mt-16">
          <Link
            to={`/services/${nextMeta.slug}`}
            className="group flex items-center justify-between border-t border-white/10 pt-8"
            data-testid="service-next-link"
          >
            <div>
              <p className="font-spacemono text-[10px] uppercase tracking-[0.3em] text-white/35">{t("servicesPage.next")}</p>
              <p className="mt-2 font-syne text-xl font-bold tracking-tight transition-colors duration-300 group-hover:text-[#F97316] md:text-2xl">
                {next.name}
              </p>
            </div>
            <span className="flex h-12 w-12 items-center justify-center rounded-full border border-white/15 transition-colors duration-300 group-hover:border-[#F97316] group-hover:bg-[#F97316]">
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
            </span>
          </Link>
        </FadeUp>
      </div>
    </main>
  );
}
